import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { getAvatarsList, getAvatarUrl } from '../lib/avatars';
import logger from '../lib/logger';
import './CreateCharacter.css';

function CreateCharacter() {
  const navigate = useNavigate();
  const { state, createCharacter, showNotification, loadCharacterClasses } = useGame();
  const { characterClasses, characterClassesLoading } = state;
  const [nickname, setNickname] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAvatars, setLoadingAvatars] = useState(true);
  const [avatars, setAvatars] = useState<{ id: string; filename: string }[]>([]);

  useEffect(() => {
    // Загружаем классы персонажей через контекст
    loadCharacterClasses();

    // Загружаем аватарки
    const loadAvatars = async () => {
      try {
        const avatarsList = await getAvatarsList();
        setAvatars(avatarsList);
      } catch (err: any) {
        logger.error('CreateCharacter', 'Failed to load avatars:', err);
        showNotification('error', 'Не удалось загрузить аватарки');
      } finally {
        setLoadingAvatars(false);
      }
    };

    loadAvatars();
  }, [loadCharacterClasses, showNotification]);

  // Следим за успешным созданием персонажа
  useEffect(() => {
    if (state.character && loading) {
      showNotification('success', 'Персонаж успешно создан!');
      navigate('/');
    }
  }, [state.character, loading, navigate, showNotification]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nickname.trim()) {
      showNotification('error', 'Введите никнейм');
      return;
    }

    if (nickname.trim().length < 3) {
      showNotification('error', 'Никнейм должен быть не менее 3 символов');
      return;
    }

    if (!selectedClass) {
      showNotification('error', 'Выберите класс персонажа');
      return;
    }

    if (!selectedAvatar) {
      showNotification('error', 'Выберите аватарку');
      return;
    }

    setLoading(true);

    // Создаем персонажа через контекст
    createCharacter({
      nickname: nickname.trim(),
      class_id: selectedClass,
      avatar: selectedAvatar
    });
  };

  if (characterClassesLoading || loadingAvatars) {
    return (
      <div className="create-character-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-character-container">
      <div className="create-character-card">
        <h1>Создание персонажа</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nickname">Никнейм</label>
            <input
              id="nickname"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Введите никнейм"
              maxLength={20}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Класс персонажа</label>
            {characterClasses.length > 0 ? (
              <div className="class-selection">
                {characterClasses.map((charClass) => (
                  <div
                    key={charClass.id}
                    className={`class-option ${selectedClass === charClass.id ? 'selected' : ''}`}
                    onClick={() => !loading && setSelectedClass(charClass.id)}
                  >
                    <h3>{charClass.name}</h3>
                    {charClass.description && <p>{charClass.description}</p>}
                    <div className="class-stats">
                      <span>Сила: {charClass.base_stats.strength}</span>
                      <span>Ловкость: {charClass.base_stats.agility}</span>
                      <span>Интуиция: {charClass.base_stats.intuition}</span>
                      <span>Выносливость: {charClass.base_stats.endurance}</span>
                      <span>Интеллект: {charClass.base_stats.intelligence}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-classes">Нет доступных классов персонажей</p>
            )}
          </div>

          <div className="form-group">
            <label>Аватарка</label>
            {avatars.length > 0 ? (
              <div className="avatar-selection">
                {avatars.map((avatar) => (
                  <div
                    key={avatar.id}
                    className={`avatar-option ${selectedAvatar === avatar.id ? 'selected' : ''}`}
                    onClick={() => !loading && setSelectedAvatar(avatar.id)}
                  >
                    <div className="avatar-image-container">
                      <img 
                        src={getAvatarUrl(avatar.id)} 
                        alt=""
                        onError={(e) => {
                          // Если изображение не загрузилось, показываем заглушку
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzMzMyIvPgogIDx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM5OTkiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg==';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-classes">Нет доступных аватарок</p>
            )}
          </div>

          <button 
            type="submit" 
            className="create-button"
            disabled={loading || !nickname.trim() || !selectedClass || !selectedAvatar || characterClasses.length === 0}
          >
            {loading ? 'Создание...' : 'Создать персонажа'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateCharacter;
